import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import { PageHeader } from '@/components/page'
import { ProviderIcon } from '@/components/provider-icon'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { useDebounced } from '@/lib/use-debounced'
import { useUsers, type AdminUser } from '@/features/users/api'
import { localDate } from '@/lib/datetime'

const PAGE_SIZE = 20

export function UsersPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const query = useUsers({ page, pageSize: PAGE_SIZE, q: useDebounced(search) })

  const columns = useMemo<ColumnDef<AdminUser, unknown>[]>(
    () => [
      {
        accessorKey: 'display_name',
        header: 'User',
        cell: ({ row }) => (
          <div>
            <div className="font-semibold">{row.original.display_name}</div>
            <div className="text-xs text-faint">{row.original.email || '—'}</div>
          </div>
        ),
      },
      {
        accessorKey: 'id',
        header: 'UID',
        cell: ({ row }) => (
          <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {row.original.id}
          </code>
        ),
      },
      {
        accessorKey: 'linked_accounts',
        header: 'Linked',
        enableSorting: false,
        cell: ({ row }) => {
          const linked = row.original.linked_accounts
          return linked.length === 0 ? (
            <span className="text-faint">—</span>
          ) : (
            <span className="flex items-center gap-1.5">
              {linked.map((provider) => (
                <ProviderIcon key={provider} provider={provider} />
              ))}
            </span>
          )
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.status === 1 ? 'success' : 'destructive'}>
            {row.original.status === 1 ? 'active' : 'disabled'}
          </Badge>
        ),
      },
      {
        accessorKey: 'joined_at',
        header: 'Joined',
        cell: ({ row }) => localDate(row.original.joined_at),
      },
      { accessorKey: 'points_balance', header: 'Points' },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        // An explicit link rather than a clickable row: the row carries selectable
        // text like the UID and the email, and making the whole thing navigate
        // turns every attempt to copy one into an accidental page change.
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button variant="secondary" size="sm" asChild>
              <Link to={`/users/${row.original.id}`}>View</Link>
            </Button>
          </div>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader title="Users" />
      <Card>
        <DataTable
          columns={columns}
          query={query}
          search={{
            value: search,
            onChange: (value) => {
              setSearch(value)
              // A narrowed list is shorter, so page 4 of the old result usually
              // does not exist in the new one.
              setPage(1)
            },
            placeholder: 'Search name, email, or UID…',
          }}
          pagination={{ page, onPageChange: setPage }}
          emptyMessage="No users returned by the backend yet."
        />
      </Card>
    </>
  )
}
